import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { SUFI_PRACTICES, SUFI_QUOTES, SUFISM_IMG } from "../lib/content";
import SectionTitle from "../components/site/SectionTitle";
import GeometricDivider from "../components/site/GeometricDivider";

const Hero = () => (
  <section className="relative pt-32 pb-24 overflow-hidden" data-testid="sufism-hero">
    <div className="absolute inset-0">
      <img src={SUFISM_IMG.whirling} alt="Whirling dervishes" className="h-full w-full object-cover opacity-30" />
      <div className="absolute inset-0 bg-gradient-to-b from-ivory via-ivory/80 to-ivory" />
    </div>
    <div className="relative container-page text-center">
      <span className="text-[11px] uppercase tracking-[0.32em] text-gold">Sufism</span>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-emerald mt-4 leading-tight">The Mystical Path</h1>
      <p className="calligraphy text-gold text-xl mt-5">Exploring the soul's journey toward the Beloved</p>
      <p className="text-charcoal-muted mt-6 max-w-3xl mx-auto text-lg leading-relaxed">
        Sufism is the mystical dimension of Islam — a journey of spiritual awakening and inner transformation rooted in the teachings of the Prophet Muhammad ﷺ, focused on the direct experience of the Divine through devotion and self-discovery.
      </p>
    </div>
  </section>
);

const Practices = () => (
  <section className="section-pad pattern-bg" data-testid="sufism-practices">
    <div className="container-page">
      <SectionTitle eyebrow="Practice" title="Love, Remembrance & the Inner Dance" subtitle="Six gentle doorways into the mystical life — each a thread weaving the seeker closer to the Divine." />
      <GeometricDivider />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {SUFI_PRACTICES.map((p, i) => {
          const Icon = Icons[p.icon] || Icons.Sparkles;
          return (
            <div key={p.title} data-testid={`practice-${i}`} className="card-soft p-8 reveal" style={{ transitionDelay: `${i * 60}ms` }}>
              <span className="grid place-items-center h-12 w-12 rounded-full border border-gold/50 text-gold mb-5">
                <Icon size={20} />
              </span>
              <h3 className="font-serif text-xl text-emerald">{p.title}</h3>
              <p className="text-charcoal-muted mt-3 text-sm leading-relaxed">{p.desc}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

const QuoteCarousel = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI(v => (v + 1) % SUFI_QUOTES.length), 6000);
    return () => clearInterval(t);
  }, []);
  return (
    <section className="pattern-emerald text-ivory section-pad" data-testid="sufism-quotes">
      <div className="container-page max-w-4xl text-center">
        <span className="text-[11px] uppercase tracking-[0.32em] text-gold">Sufi Poetry</span>
        <div className="min-h-[200px] mt-8 flex flex-col items-center justify-center">
          <Icons.Quote className="text-gold mb-6" size={32} />
          <blockquote className="font-serif text-2xl md:text-4xl leading-snug transition-opacity duration-700" key={i}>
            "{SUFI_QUOTES[i].q}"
          </blockquote>
          <cite className="block mt-6 text-gold not-italic">— {SUFI_QUOTES[i].a}</cite>
        </div>
        <div className="flex items-center justify-center gap-2 mt-10">
          {SUFI_QUOTES.map((q, idx) => (
            <button
              key={`${q.a}-${idx}`}
              data-testid={`quote-dot-${idx}`}
              onClick={() => setI(idx)}
              className={`h-2 rounded-full transition-all ${idx === i ? "w-10 bg-gold" : "w-2 bg-ivory/30 hover:bg-ivory/60"}`}
              aria-label={`Quote ${idx + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const Orders = () => (
  <section className="section-pad" data-testid="sufism-orders">
    <div className="container-page grid md:grid-cols-2 gap-12 items-center">
      <div className="reveal">
        <img src={SUFISM_IMG.lantern} alt="Ornate brass lantern" className="rounded-lg w-full object-cover shadow-2xl" />
      </div>
      <div className="reveal">
        <span className="text-[11px] uppercase tracking-[0.32em] text-gold">Tariqas & Luminaries</span>
        <h2 className="font-serif text-3xl md:text-4xl text-emerald mt-4 leading-tight">Orders that have lit the way</h2>
        <p className="text-charcoal-muted mt-6 leading-relaxed">
          <strong className="text-emerald">Mevlevi, Naqshbandi, Chishti</strong>, and <strong className="text-emerald">Qadiri</strong> orders guide seekers across centuries. Luminaries such as <em>Rumi, Al-Ghazali, Rabia Basri</em>, and <em>Shams Tabrizi</em> remain torchbearers of love and remembrance.
        </p>
        <p className="text-charcoal-muted mt-4 leading-relaxed">
          Each tariqa is a doorway — different in form, one in essence — leading the heart back to the Beloved.
        </p>
      </div>
    </div>
  </section>
);

const Invitation = () => (
  <section className="pattern-bg section-pad" data-testid="sufism-invite">
    <div className="container-page max-w-3xl text-center reveal">
      <span className="text-[11px] uppercase tracking-[0.32em] text-gold">An Invitation</span>
      <h2 className="font-serif text-3xl md:text-5xl text-emerald mt-4 leading-tight">Embrace the Sufi Path</h2>
      <p className="text-charcoal-muted mt-6 text-lg leading-relaxed">
        Cultivate love. Seek profound connection with the Divine. This is a transformative journey toward inner peace, harmony, and divine union — open to every sincere heart.
      </p>
    </div>
  </section>
);

export default function Sufism() {
  useEffect(() => { document.title = "Sufism — The Mystical Path"; }, []);
  return (
    <div data-testid="sufism-page">
      <Hero />
      <Practices />
      <QuoteCarousel />
      <Orders />
      <Invitation />
    </div>
  );
}
